/**
 * Loader do SDK de criptografia de cartão do PagBank.
 *
 * O SDK criptografa os dados do cartão NO BROWSER com a chave pública RSA da
 * conta — o backend recebe só o blob `encrypted`, o PAN/CVV nunca saem daqui
 * (checklist PCI). Injetado por <script> (sem dependência npm; a CSP em prod
 * precisa liberar `assets.pagseguro.com.br` em script-src — registrado no
 * Caddyfile).
 *
 * §10 do CLAUDE.md: falha de SDK tem que virar estado VISÍVEL com saída — por
 * isso o load tem timeout e rejeita explicitamente, e o encrypt distingue erro
 * de validação (campos) de erro de infra. O caller (CardStep) mostra a saída
 * pro PIX quando o SDK não carrega.
 */

const SDK_URL =
  'https://assets.pagseguro.com.br/checkout-sdk-js/rc/dist/browser/pagseguro.min.js';
const LOAD_TIMEOUT_MS = 8000;

interface EncryptCardInput {
  publicKey: string;
  holder: string;
  number: string;
  expMonth: string;
  expYear: string;
  securityCode: string;
}

interface EncryptCardResult {
  encryptedCard: string;
  hasErrors: boolean;
  errors: { code: string; message: string }[];
}

interface PagSeguroSdk {
  encryptCard: (input: EncryptCardInput) => EncryptCardResult;
}

declare global {
  interface Window {
    PagSeguro?: PagSeguroSdk;
  }
}

let loadPromise: Promise<PagSeguroSdk> | null = null;

/** Carrega o SDK (single-flight). Rejeita em timeout/erro de rede. */
export function loadPagbankSdk(): Promise<PagSeguroSdk> {
  if (window.PagSeguro) return Promise.resolve(window.PagSeguro);
  if (loadPromise) return loadPromise;

  loadPromise = new Promise<PagSeguroSdk>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SDK_URL}"]`
    );
    const script = existing ?? document.createElement('script');
    let settled = false;

    const timer = window.setTimeout(() => {
      if (settled) return;
      settled = true;
      loadPromise = null; // permite retry
      reject(new Error('pagbank_sdk_timeout'));
    }, LOAD_TIMEOUT_MS);

    const onOk = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      if (window.PagSeguro) resolve(window.PagSeguro);
      else {
        loadPromise = null;
        reject(new Error('pagbank_sdk_no_global'));
      }
    };
    const onErr = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      loadPromise = null;
      reject(new Error('pagbank_sdk_load_error'));
    };

    script.addEventListener('load', onOk);
    script.addEventListener('error', onErr);
    if (!existing) {
      script.src = SDK_URL;
      script.async = true;
      document.head.appendChild(script);
    } else if (window.PagSeguro) {
      onOk(); // já estava carregado
    }
  });
  return loadPromise;
}

export class CardValidationError extends Error {
  constructor(public readonly fields: { code: string; message: string }[]) {
    super('card_validation');
  }
}

/**
 * Criptografa o cartão. Lança `CardValidationError` (campos inválidos — o
 * cliente corrige) ou Error de infra (SDK não carregou — cai no fallback PIX).
 * Retorna só o blob `encrypted`; o PAN/CVV nunca sobem.
 */
export async function encryptCard(input: EncryptCardInput): Promise<string> {
  const sdk = await loadPagbankSdk();
  const result = sdk.encryptCard(input);
  if (result.hasErrors || !result.encryptedCard) {
    throw new CardValidationError(result.errors ?? []);
  }
  return result.encryptedCard;
}
